import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Search, X } from "lucide-react";
import api, { fileUrl } from "../lib/api";

export default function SearchBar() {
    const [q, setQ] = useState("");
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState({ users: [], posts: [] });
    const [loading, setLoading] = useState(false);
    const ref = useRef();
    const timer = useRef();

    useEffect(() => {
        if (timer.current) clearTimeout(timer.current);
        if (q.trim().length < 2) { setResults({ users: [], posts: [] }); return; }
        timer.current = setTimeout(async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/search?q=${encodeURIComponent(q.trim())}`);
                setResults(data);
            } catch (e) { /* ignore */ }
            finally { setLoading(false); }
        }, 250);
    }, [q]);

    useEffect(() => {
        const close = (e) => {
            if (!ref.current || ref.current.contains(e.target)) return;
            setOpen(false);
        };
        if (open) document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, [open]);

    const hasResults = results.users.length + results.posts.length > 0;

    return (
        <div className="relative flex-1 max-w-xs sm:max-w-md" ref={ref}>
            <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
                <input
                    type="search"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder="Search @username or captions…"
                    className="w-full pl-9 pr-8 py-2 rounded-lg brut-inset bg-white font-medium text-sm focus:outline-none"
                    data-testid="search-input"
                />
                {q && (
                    <button
                        onClick={() => { setQ(""); setOpen(false); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[#FFE55C]"
                        aria-label="Clear"
                        data-testid="search-clear"
                    >
                        <X className="w-3.5 h-3.5" />
                    </button>
                )}
            </div>
            {open && q.trim().length >= 2 && (
                <div className="absolute top-full left-0 right-0 mt-2 brut-lg bg-white rounded-xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto" data-testid="search-results">
                    {loading && <div className="p-3 text-sm font-medium text-[#4A4A4A]">Searching…</div>}
                    {!loading && !hasResults && (
                        <div className="p-4 text-sm font-medium text-[#4A4A4A] italic">No matches for &ldquo;{q}&rdquo;.</div>
                    )}
                    {results.users.length > 0 && (
                        <div className="p-2">
                            <div className="text-[10px] font-black uppercase tracking-widest text-[#4A4A4A] px-2 py-1">Users</div>
                            <ul>
                                {results.users.map((u) => (
                                    <li key={u.username}>
                                        <Link
                                            to={`/u/${u.username}`}
                                            onClick={() => setOpen(false)}
                                            className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#FFFDF7]"
                                            data-testid={`search-user-${u.username}`}
                                        >
                                            <div className="w-8 h-8 rounded-full bg-[#B8E1FF] border-2 border-[#0A0A0A] flex items-center justify-center font-black text-xs">
                                                {u.username[0].toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-sm truncate">@{u.username}</div>
                                                {u.bio && <div className="text-[11px] text-[#4A4A4A] truncate">{u.bio}</div>}
                                            </div>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {results.posts.length > 0 && (
                        <div className="p-2 border-t-2 border-[#0A0A0A]/10">
                            <div className="text-[10px] font-black uppercase tracking-widest text-[#4A4A4A] px-2 py-1">Posts</div>
                            <div className="grid grid-cols-3 gap-2 p-2">
                                {results.posts.slice(0, 9).map((p) => (
                                    <Link
                                        key={p.id}
                                        to={`/p/${p.id}`}
                                        onClick={() => setOpen(false)}
                                        className="block relative aspect-square rounded-lg overflow-hidden brut-inset"
                                        data-testid={`search-post-${p.id}`}
                                    >
                                        <img src={fileUrl(p.image_path)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                        <div className="absolute bottom-1 right-1 bg-[#FFE55C] border border-[#0A0A0A] rounded-full px-1.5 py-0.5 text-[9px] font-black">
                                            {p.avg_rating.toFixed(1)}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
